import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Refreshes the two queries the client's "spots available" number is derived from: the shift's
 * signups (occupancy) and the event's shifts (maxSignup).
 *
 * Call this after a signup request is rejected as well as after one succeeds. The server's capacity
 * gate reads live data, so a rejection is proof the cached count is wrong. Without a refresh the
 * page keeps the stale number, leaves the add buttons enabled, and every retry fails identically —
 * the only place the true count exists is the error message the user just read.
 *
 * Refreshing on a user action rather than a timer is deliberate: the app-wide staleTime of Infinity
 * exists so content does not shift underneath the user (see index.tsx), and polling would reintroduce
 * exactly that.
 */
export function useRefreshSignupCapacity(eventId: string) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['RedVelvetApi', 'getSignupsByShiftId'],
    });
    queryClient.invalidateQueries({
      queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId }],
    });
  }, [queryClient, eventId]);
}
